/**
 * Every sentence of the composer, the brief conversation, the brief card and
 * the ad's page (docs/SPEC.md §4, §5.5, §6, §14.1). Examples are fictional
 * (§14.6, owner 2026-10-14): never a real company, never an Intelibo product.
 */
import type { AdState, DetailKey, Provenance } from "../api/ads";

// ------------------------------------------------------------- the composer

export const COMPOSER = {
  eyebrow: "— New ad",
  title: "What is the ad about?",
  boxLabel: "What is the ad about?",
  boxPlaceholder:
    "An ad for Tidewell, a steel bottle that keeps water cold for 24 hours. Or a web address. Or a few photos. Or describe the film scene by scene.",
  boxHelper:
    "Type anything, paste a web address, or attach photos. TrueReel asks at most three questions; the brief then becomes the film, each scene playing as soon as it is built.",
  /** §4.2: Start is disabled with this sentence while nothing is given. */
  empty:
    "Say a word, paste a web address or attach something, and TrueReel does the rest.",
  start: "Start",
  starting: "Starting…",
  uploading: "Waiting for the attachments to upload.",
  // the site
  siteReading: "Reading {host}…",
  siteFailed:
    "{host} could not be read. Start anyway: the brief goes on without it.",
  siteLogo: "Logo found",
  siteColours: "{n} colours",
  siteColour: "1 colour",
  sitePictures: "{n} pictures",
  sitePicture: "1 picture",
  siteTagline: "tagline",
  siteFonts: "{fonts}",
  siteRemove: "Leave {host} out",
  siteFrom: "From your site",
  // what each chip found, opened under the chips (2026-10-24 fix(composer) site chips)
  siteShow: "Show what was found: {what}",
  siteClose: "Close",
  siteLogoYours: "Your logo is used",
  siteLogoKit: "{name}'s logo is used",
  siteLogoMine: "Your logo",
  siteLogoOnSite: "Found on {host}",
  siteLogoUsed: "Used in the film",
  siteLogoNotUsed: "Not used",
  siteLogoKitUsed:
    "The film uses the logo of the brand kit {name}, not this one. Choose None to use this one.",
  siteLogoYoursUsed:
    "The film uses the logo you attached. Remove it from the attachments to use this one.",
  sitePicturesNone: "no pictures",
  sitePicturesHint:
    "The pictures found on {host}. Remove any the film should not use; the AI never sees it.",
  sitePictureAlt: "Picture {n} from {host}",
  sitePictureRemove: "Leave picture {n} out",
  sitePicturesBack: "Put back the pictures left out ({n})",
  siteTaglineOut: "no tagline",
  siteTaglineHint: "The first sentences of {host}. The brief may quote them.",
  siteTaglineRemove: "Leave the tagline out",
  siteTaglineBack: "Put the tagline back",
  siteTaglineGone: "Left out: the AI never sees these sentences.",
  siteColoursHint: "The colours of {host}, for the film's look.",
  siteFontsHint: "The fonts {host} names.",
  // attachments
  attach: "Attach",
  attachPhotos: "Photos or a logo",
  attachPhotosHint: "PNG, JPEG or WebP, up to 25 MB each",
  attachSheet: "A spreadsheet",
  attachSheetHint: "CSV or XLSX: numbers the film can show",
  attachReference: "A reference",
  attachReferenceHint:
    "Pictures of a film or an ad you like: it is looked at, never copied. For now attach a few stills of it; videos come later.",
  attachModel: "A 3D model goes to Objects as an import.",
  attachModelLink: "Bring a model in",
  attachLogo: "Logo",
  attachLogoIs:
    "{name} is your logo: the film uses it. Press to use it as a photo.",
  attachLogoMake: "Use {name} as your logo",
  sheetRole: "spreadsheet",
  referenceRole: "reference",
  notSheet: "That file is not a CSV or XLSX spreadsheet.",
  // details
  details: "Details",
  detailsHint: "All optional. Anything left empty is found or decided.",
  // options
  length: "Length",
  aspect: "Aspect",
  brandKit: "Brand kit",
  brandKitNone: "None",
  brandKitDefault: "{name} (default)",
  brandKitHelperKit:
    "The film uses {name}'s colours, fonts and logo. Choose None for an ad about something else.",
  brandKitHelperNone:
    "No brand kit: the colours and the name come from what you say, your site or the AI.",
  noKits: "No brand kit yet.",
  noKitsLink: "Keep your logo, colours and fonts",
  template: "Template",
  templateNone: "No template",
  templateNoneHint: "The film is made from what you say.",
  // Objects chosen before the brief ("Use in a new ad", §23)
  cast: "In the film",
  castHint: "From Objects: the film is made around it.",
} as const;

export const DETAIL_LABELS: Record<DetailKey, string> = {
  subject: "Product or subject",
  what: "What it is",
  audience: "Who it is for",
  better: "The one thing it does better",
  call: "What to do at the end",
};

export const DETAIL_PLACEHOLDERS: Record<DetailKey, string> = {
  subject: "Tidewell bottle",
  what: "A steel water bottle",
  audience: "Hikers and commuters",
  better: "Keeps water cold for 24 hours",
  call: "Visit tidewell.example",
};

export const ASPECT_NAMES: Record<string, string> = {
  "9:16": "9:16 tall",
  "1:1": "1:1 square",
  "16:9": "16:9 wide",
};

// ------------------------------------------------------------- the ad's page

export const STATE_LABELS: Record<AdState, string> = {
  brief: "Brief",
  building: "Making the film",
  ready: "Ready",
  failed: "Stopped",
};

export const PAGE = {
  eyebrow: "— Ad",
  youSaid: "What you said",
  nothingSaid: "Nothing typed: TrueReel starts from what you gave.",
  brandKit: "Brand kit: {name}",
  noBrandKit: "No brand kit",
  editName: "Edit name",
  nameLabel: "Name",
  attachments: "{n} attachments",
  attachment: "1 attachment",
  site: "Site: {host}",
  reconnecting: "Reconnecting…",
  notFound: "This ad does not exist, or it was deleted.",
  backToAds: "Your ads",
} as const;

// ------------------------------------------------------- the conversation

export const CONVERSATION = {
  title: "The conversation",
  starting: "Reading what you gave…",
  thinking: "Thinking…",
  you: "You",
  trueReel: "TrueReel",
  thatsRight: "That's right",
  chooseThis: "Choose this",
  youDecide: "You decide",
  youDecideHint: "The AI's choices for everything still open.",
  freeLabel: "Or say it in your words",
  freePlaceholder: "Something else…",
  send: "Send",
  questionOf: "Question {n} of at most 3",
  final: "The brief is ready.",
  edited: "Changed on the card",
  accepted: "Accepted a suggestion",
} as const;

// ------------------------------------------------------------ the card (§5.5)

export const CARD = {
  title: "The brief",
  waiting: "The brief fills in as the conversation goes.",
  edit: "Edit",
  save: "Save",
  cancel: "Cancel",
  saving: "Saving…",
  accept: "Accept",
  drop: "Drop",
  locked:
    "The brief cannot be changed just now. Once the film plays, change a line here or say it in the conversation.",
  // line labels
  subject: "Subject",
  promise: "Promise",
  audience: "For",
  mood: "Mood",
  length: "Length",
  look: "Look",
  end_card: "Ends with",
  scenes: "Scenes",
  cast: "Cast",
  sound: "Sound",
  suggested: "Suggested",
  /** What accepting a suggestion does, by its kind (§5.5). */
  suggestionKind: {
    caption: "On screen",
    staging: "To show",
  },
  /** The field Accept opens on a suggestion that asks for a value (`needs`):
   *  its label. `backend/brief/rules.py`'s `NEEDS_SENTENCES` holds the same
   *  words. */
  needs: {
    price: "Price to show at the end",
    discount: "Discount to show at the end",
    phone: "Phone number to show at the end",
    address: "Address to show at the end",
    hours: "Opening hours to show at the end",
    date: "Date to show at the end",
  },
  needsAdd: "Add",
  // values
  lookKit: 'Brand kit "{name}"',
  lookColours: "{n} colours",
  lookColour: "1 colour",
  noMusic: "no music",
  music: "{music} music",
  musicDecide: "music the AI chooses",
  noVoice: "no voice",
  voice: "a {character} voice",
  sfx: "sound effects",
  sceneLine: "{n} {name} · {seconds} s",
  moodHint: "Words, separated by commas",
  endName: "Name",
  endNoName: "No name yet",
  endAddName: "No name yet — add it",
  endUrl: "Web address",
  endTagline: "Tagline",
  sceneName: "Name",
  sceneWhat: "What happens",
  sceneCaptions: "Captions, one per line",
  castWhat: "What it is",
  musicLabel: "Music",
  voiceLabel: "Voice",
} as const;

export const MUSIC_NAMES: Record<string, string> = {
  calm: "calm",
  energetic: "energetic",
  none: "none",
  decide: "the AI decides",
};

export const VOICE_NAMES: Record<string, string> = {
  warm: "warm",
  clear: "clear",
  none: "none",
};

/** The provenance a line shows (§5.5). */
export const PROVENANCE: Record<Provenance, string> = {
  said: "you said",
  site: "from your site",
  sheet: "from your spreadsheet",
  photo: "from your photos",
  reference: "from your reference",
  brand: "brand",
  template: "from the template",
  decided: "the AI's choice",
};

export function provenanceOf(from: string | null | undefined): string | null {
  if (!from) return null;
  const head = from.split(":")[0] as Provenance;
  return Object.prototype.hasOwnProperty.call(PROVENANCE, head)
    ? PROVENANCE[head]
    : null;
}

// ------------------------------------------------------------ failed

export const FILM = {
  failed: "The film could not be made.",
  /** §6.2: bakes again (free), or writes the film again when it never was. */
  tryAgain: "Try again",
  trying: "Starting…",
} as const;

// ------------------------------------------------------------ the list

export const ADS = {
  eyebrow: "— Ads",
  title: "Your ads",
  newAd: "New ad",
  empty: "No ads yet.",
  emptyHint:
    "Say what you want in a sentence, a web address or a few photos. A short conversation turns it into a brief, then a film.",
  first: "Make your first ad",
  noFilm: "No film yet",
  untitled: "Your ad",
} as const;

/** `{key}` filled from `params`; numbers with thousands separators. */
export function say(
  sentence: string,
  params: Record<string, string | number> = {},
): string {
  return sentence.replace(/\{(\w+)\}/g, (whole, key: string) => {
    if (!Object.prototype.hasOwnProperty.call(params, key)) return whole;
    const value = params[key];
    return typeof value === "number"
      ? value.toLocaleString("en-US")
      : String(value);
  });
}
